import { parseProductRichText, productRichTextPlainText, } from './product-rich-text.js';
export const AD_LANDING_LIMITS = Object.freeze({
    imageBytes: 10 * 1024 * 1024,
    videoBytes: 50 * 1024 * 1024,
    videoDurationMs: 300_000,
    maxBlocks: 100,
    maxTextLength: 20_000,
    maxNodes: 1_200,
    maxJsonBytes: 512 * 1024,
});
const uuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/iu;
function object(value, keys) {
    if (!value || typeof value !== 'object' || Array.isArray(value))
        throw new Error('落地页内容格式不正确');
    const record = value;
    if (Object.keys(record).some((key) => !keys.includes(key)))
        throw new Error('落地页包含不支持的设置');
    return record;
}
function text(value, max, label) {
    if (typeof value !== 'string' ||
        value.length > max ||
        [...value].some((character) => character.charCodeAt(0) < 32 && ![9, 10, 13].includes(character.charCodeAt(0))))
        throw new Error(`${label}格式不正确或过长`);
    return value.trim();
}
function assetId(value) {
    if (typeof value !== 'string' || !uuid.test(value))
        throw new Error('请选择本落地页上传的素材');
    return value.toLowerCase();
}
function layout(record) {
    const width = record.width ?? '100%';
    const align = record.align ?? 'center';
    if (!['25%', '50%', '75%', '100%'].includes(width) ||
        !['left', 'center', 'right'].includes(align))
        throw new Error('素材宽度或对齐方式不正确');
    return { width: width, align: align };
}
/** Drafts may be incomplete. Publishing additionally checks title, content and ready owned assets. */
export function parseAdLandingContent(value) {
    let serialized;
    try {
        serialized = JSON.stringify(value);
    }
    catch {
        throw new Error('落地页内容格式不正确');
    }
    if (typeof serialized !== 'string' ||
        new TextEncoder().encode(serialized).length > AD_LANDING_LIMITS.maxJsonBytes)
        throw new Error('落地页内容过大，请减少内容');
    const root = object(value, ['version', 'title', 'summary', 'coverAssetId', 'blocks']);
    if (root.version !== 1 ||
        !Array.isArray(root.blocks) ||
        root.blocks.length > AD_LANDING_LIMITS.maxBlocks)
        throw new Error('落地页内容版本或段落数量不正确');
    let nodes = 0;
    let letters = 0;
    const ids = new Set();
    const inspect = (node) => {
        nodes++;
        if (node.type === 'image')
            throw new Error('请使用图片段落添加本落地页上传的图片');
        node.content?.forEach(inspect);
    };
    const blocks = root.blocks.map((item) => {
        const record = object(item, [
            'id',
            'type',
            'document',
            'assetId',
            'posterAssetId',
            'alt',
            'width',
            'align',
        ]);
        const id = text(record.id, 80, '段落编号');
        if (!/^[a-zA-Z0-9_-]{1,80}$/u.test(id) || ids.has(id))
            throw new Error('段落编号重复或格式不正确');
        ids.add(id);
        if (record.type === 'richText') {
            object(record, ['id', 'type', 'document']);
            const document = parseProductRichText(record.document);
            inspect(document);
            letters += productRichTextPlainText(document).length;
            return { id, type: 'richText', document };
        }
        if (record.type === 'image') {
            object(record, ['id', 'type', 'assetId', 'alt', 'width', 'align']);
            return {
                id,
                type: 'image',
                assetId: assetId(record.assetId),
                alt: text(record.alt ?? '', 500, '图片说明'),
                ...layout(record),
            };
        }
        if (record.type === 'video') {
            object(record, ['id', 'type', 'assetId', 'posterAssetId', 'width', 'align']);
            return {
                id,
                type: 'video',
                assetId: assetId(record.assetId),
                ...(record.posterAssetId == null ? {} : { posterAssetId: assetId(record.posterAssetId) }),
                ...layout(record),
            };
        }
        throw new Error('落地页包含不支持的段落');
    });
    if (nodes > AD_LANDING_LIMITS.maxNodes || letters > AD_LANDING_LIMITS.maxTextLength)
        throw new Error('落地页文字或排版过多，请适当精简');
    return {
        version: 1,
        title: text(root.title, 100, '落地页标题'),
        summary: text(root.summary, 500, '分享简介'),
        coverAssetId: root.coverAssetId == null ? null : assetId(root.coverAssetId),
        blocks,
    };
}
export function adLandingAssetIds(content) {
    return [
        ...new Set([
            ...(content.coverAssetId ? [content.coverAssetId] : []),
            ...content.blocks.flatMap((block) => block.type === 'richText'
                ? []
                : [
                    block.assetId,
                    ...(block.type === 'video' && block.posterAssetId ? [block.posterAssetId] : []),
                ]),
        ]),
    ];
}
export function validateAdLandingForPublish(content) {
    if (!content.title.trim())
        throw new Error('请填写落地页标题');
    if (!content.blocks.length ||
        !content.blocks.some((block) => block.type !== 'richText' || productRichTextPlainText(block.document).trim()))
        throw new Error('请添加介绍文字、图片或视频后再发布');
}
export function emptyAdLandingContent(title = '') {
    return { version: 1, title, summary: '', coverAssetId: null, blocks: [] };
}
//# sourceMappingURL=ad-landing.js.map